import { useState } from 'react';
import { PhoneIncoming, SlidersHorizontal, BookOpen, PhoneForwarded } from 'lucide-react';
import PageHeader from '../components/PageHeader.jsx';
import CallActionsEditor from '../components/CallActionsEditor.jsx';
import RoutingEditor from '../components/RoutingEditor.jsx';
import TransferDirectoryEditor from '../components/TransferDirectoryEditor.jsx';
import BusinessInfoEditor from '../components/BusinessInfoEditor.jsx';
import FaqEditor from '../components/FaqEditor.jsx';
import KnowledgeEditor from '../components/KnowledgeEditor.jsx';

// 📞 CALL ROUTING — how INBOUND calls are handled.
//
// Two sub-tabs keep the page focused:
//   • Call handling — the per-action config: Provide information, Take a message,
//     Forward, Voicemail, Schedule (CallActionsEditor + RoutingEditor).
//   • Transfer directory — who the AI can hand a LIVE caller to (`transfer_targets`).
//     Its own tab rather than a card under Call handling, because it is a different
//     store from RoutingEditor's `forwarding.contacts`: the panel reads
//     transfer_targets and never reads the routing JSON's contacts.
//   • Answer material — the AI's answer source (Business Info + FAQ + Knowledge),
//     re-homed here from Settings because this is what "Provide information to
//     the caller" draws on.
const TABS = [
  { id: 'handling', label: 'Call handling', icon: SlidersHorizontal },
  { id: 'transfer', label: 'Transfer directory', icon: PhoneForwarded },
  { id: 'material', label: 'Answer material', icon: BookOpen }
];

export default function CallRouting() {
  const [tab, setTab] = useState('handling');

  return (
    <div className="px-6 md:px-10 py-8 max-w-4xl">
      <PageHeader
        title="Call Knowledge"
        subtitle="What the AI knows and how it handles your inbound calls — what it answers from, and what it can do"
      />

      <div className="flex items-center gap-2 mb-6 border-b border-stone-900">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-3 py-2 text-sm -mb-px border-b-2 ${
              tab === id
                ? 'border-alfred-gold text-stone-100'
                : 'border-transparent text-stone-400 hover:text-stone-200'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {tab === 'handling' && (
        <div className="space-y-8">
          <div className="flex items-start gap-3 rounded-xl border border-stone-800 bg-stone-900/40 p-4">
            <PhoneIncoming size={18} className="text-alfred-gold mt-0.5 shrink-0" />
            <p className="text-sm text-stone-400">
              These settings decide what the AI does for each caller: answer their
              questions, take a message, forward to a person, send to voicemail, or
              offer to schedule. Turn on what you need and fill in the details.
            </p>
          </div>
          <CallActionsEditor />
          <RoutingEditor />
        </div>
      )}

      {tab === 'transfer' && (
        <div className="space-y-8">
          <div className="flex items-start gap-3 rounded-xl border border-stone-800 bg-stone-900/40 p-4">
            <PhoneForwarded size={18} className="text-alfred-gold mt-0.5 shrink-0" />
            <p className="text-sm text-stone-400">
              People the AI can transfer a live caller to. On a <strong>warm transfer</strong>,
              the AI puts the caller on hold, calls the person to confirm they're available,
              then bridges the two together — otherwise it takes a message or sends to voicemail.
              This applies to <strong>inbound calls only</strong> — the AI never transfers on a
              call it placed itself.
            </p>
          </div>
          <TransferDirectoryEditor />
        </div>
      )}

      {tab === 'material' && (
        <div className="space-y-8">
          <p className="text-sm text-stone-400">
            When the AI <strong>provides information to the caller</strong>, it
            answers from these. Keep them accurate and the AI stays on-script.
          </p>
          <BusinessInfoEditor />
          <FaqEditor />
          <KnowledgeEditor />
        </div>
      )}
    </div>
  );
}
